import { FormEvent, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  deleteGrainConfig,
  GrainLimits,
  Godown,
  upsertGrainConfig,
} from "../../lib/api";
import { ErrorNote } from "./ui";

/** Per-godown grain-parameter editor — the portal twin of the mobile
 * app's per-grain limits tabs. A district head configures, for each
 * grain a godown accepts, the seven FCI FAQ limits used to grade a
 * sample. Each grain saves independently (PUT
 * /godowns/:id/grain-configs/:grain), so editing "wheat" never
 * touches "maize". A godown must keep at least one grain, so the last
 * one can't be removed. */

const LIMIT_FIELDS: { key: keyof GrainLimits; label: string; hint: string }[] =
  [
    { key: "moisture", label: "Moisture", hint: "% max" },
    { key: "dd", label: "DD", hint: "damaged/discoloured % max" },
    { key: "b", label: "B", hint: "broken % max" },
    { key: "i", label: "I", hint: "immature % max" },
    { key: "ddbi", label: "DD+B+I", hint: "combined % max" },
    { key: "fm", label: "FM", hint: "foreign matter % max" },
    { key: "idk", label: "IDK", hint: "insect-damaged % max" },
  ];

const DEFAULT_LIMITS: GrainLimits = {
  moisture: 14,
  dd: 3,
  b: 3,
  i: 3,
  ddbi: 8,
  fm: 1,
  idk: 1,
};

const GRAIN_PRESETS = [
  "maize",
  "wheat",
  "rice",
  "paddy",
  "soybean",
  "barley",
  "sorghum",
  "bajra",
];

export function GrainLimitsForm({ godown }: { godown: Godown }) {
  const configs = godown.grain_configs ?? [];
  const grains = configs.map((c) => c.grain_type);
  const [active, setActive] = useState<string>(grains[0] ?? "");

  // Keep an active tab even as the godown refetches. If the active
  // grain was just deleted, fall back to the first remaining one.
  const activeGrain = grains.includes(active) ? active : grains[0] ?? "";
  const activeConfig = configs.find((c) => c.grain_type === activeGrain);

  return (
    <div className="border border-gray-200 rounded-md p-3 bg-gray-50/60 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Grain parameters
        </div>
        <AddGrain
          godownId={godown.id}
          existing={grains}
          onAdded={(g) => setActive(g)}
        />
      </div>

      {grains.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {grains.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setActive(g)}
              className={
                "px-3 py-1 rounded-full text-xs font-medium capitalize " +
                (g === activeGrain
                  ? "bg-agrilens-green text-white"
                  : "bg-white text-gray-600 border border-gray-300 hover:bg-gray-100")
              }
            >
              {g}
            </button>
          ))}
        </div>
      )}

      {activeConfig ? (
        <GrainEditor
          key={`${godown.id}-${activeGrain}`}
          godownId={godown.id}
          grainType={activeGrain}
          initial={activeConfig.limits}
          canDelete={grains.length > 1}
        />
      ) : (
        <div className="text-xs text-gray-500">
          No grains configured. Add one above.
        </div>
      )}
    </div>
  );
}

function GrainEditor({
  godownId,
  grainType,
  initial,
  canDelete,
}: {
  godownId: number;
  grainType: string;
  initial: GrainLimits;
  canDelete: boolean;
}) {
  const qc = useQueryClient();
  // Edit as strings so the inputs can be cleared/typed freely; parse
  // on save. Seed from the saved limits.
  const [vals, setVals] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      LIMIT_FIELDS.map((f) => [f.key, String(initial[f.key] ?? "")]),
    ),
  );

  const refresh = () => qc.invalidateQueries({ queryKey: ["godowns"] });

  const saveMut = useMutation({
    mutationFn: () => {
      const limits = {} as GrainLimits;
      for (const f of LIMIT_FIELDS) {
        const n = parseFloat(vals[f.key]);
        limits[f.key] = Number.isFinite(n) && n >= 0 ? n : DEFAULT_LIMITS[f.key];
      }
      return upsertGrainConfig(godownId, grainType, limits);
    },
    onSuccess: refresh,
  });

  const delMut = useMutation({
    mutationFn: () => deleteGrainConfig(godownId, grainType),
    onSuccess: refresh,
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (saveMut.isPending) return;
    saveMut.mutate();
  };

  const dirty = useMemo(
    () =>
      LIMIT_FIELDS.some(
        (f) => parseFloat(vals[f.key]) !== Number(initial[f.key]),
      ),
    [vals, initial],
  );

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {LIMIT_FIELDS.map((f) => (
          <label key={f.key} className="block">
            <span
              className="block text-[11px] text-gray-500 mb-0.5"
              title={f.hint}
            >
              {f.label}
            </span>
            <input
              type="number"
              step="0.1"
              min="0"
              inputMode="decimal"
              value={vals[f.key]}
              onChange={(e) =>
                setVals((v) => ({ ...v, [f.key]: e.target.value }))
              }
              className="w-full border rounded px-2 py-1 text-sm"
            />
          </label>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={saveMut.isPending || !dirty}
          className="bg-agrilens-green text-white text-sm font-medium px-4 py-1.5 rounded hover:bg-agrilens-green-dark disabled:opacity-40"
        >
          {saveMut.isPending
            ? "Saving…"
            : dirty
              ? "Save limits"
              : "Saved"}
        </button>
        {canDelete && (
          <button
            type="button"
            disabled={delMut.isPending}
            onClick={() => delMut.mutate()}
            className="text-sm text-red-600 border border-red-200 px-3 py-1.5 rounded hover:bg-red-50 disabled:opacity-40"
            title={`Remove ${grainType} from this godown`}
          >
            {delMut.isPending ? "Removing…" : "Remove grain"}
          </button>
        )}
      </div>

      {saveMut.isError && <ErrorNote error={saveMut.error} />}
      {delMut.isError && <ErrorNote error={delMut.error} />}
    </form>
  );
}

function AddGrain({
  godownId,
  existing,
  onAdded,
}: {
  godownId: number;
  existing: string[];
  onAdded: (grain: string) => void;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");

  const options = GRAIN_PRESETS.filter((g) => !existing.includes(g));

  const mut = useMutation({
    mutationFn: (grain: string) =>
      upsertGrainConfig(godownId, grain, DEFAULT_LIMITS),
    onSuccess: (_data, grain) => {
      qc.invalidateQueries({ queryKey: ["godowns"] });
      onAdded(grain);
      setName("");
      setOpen(false);
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const grain = name.trim().toLowerCase();
    if (!grain || mut.isPending) return;
    if (existing.includes(grain)) {
      setOpen(false);
      onAdded(grain);
      return;
    }
    mut.mutate(grain);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs text-agrilens-green hover:underline"
      >
        + Add grain
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="flex items-center gap-1">
      <input
        list={`grain-presets-${godownId}`}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="grain type"
        autoFocus
        className="border rounded px-2 py-1 text-xs w-32"
      />
      <datalist id={`grain-presets-${godownId}`}>
        {options.map((g) => (
          <option key={g} value={g} />
        ))}
      </datalist>
      <button
        type="submit"
        disabled={mut.isPending}
        className="bg-agrilens-green text-white text-xs px-2 py-1 rounded hover:bg-agrilens-green-dark disabled:opacity-40"
      >
        {mut.isPending ? "…" : "Add"}
      </button>
      <button
        type="button"
        onClick={() => {
          setOpen(false);
          setName("");
        }}
        className="text-xs text-gray-400 hover:text-gray-600 px-1"
      >
        ✕
      </button>
    </form>
  );
}
